import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from '../../context/LocationContext';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import '../../css/locationModal.css';

// Curated list of popular shopping cities across India
const POPULAR_CITIES = [
  { city: "Ahmedgarh", state: "Punjab", area: "Main Market / Ward 11", pincode: "148021", lat: 30.6826, lng: 75.8302 },
  { city: "Ludhiana", state: "Punjab", area: "Ghumar Mandi / Model Town", pincode: "141001", lat: 30.9010, lng: 75.8573 },
  { city: "Delhi NCR", state: "Delhi", area: "Connaught Place / Central Delhi", pincode: "110001", lat: 28.6139, lng: 77.2090 },
  { city: "Mumbai", state: "Maharashtra", area: "Bandra / Andheri", pincode: "400050", lat: 19.0760, lng: 72.8777 },
  { city: "Bengaluru", state: "Karnataka", area: "Indiranagar / Koramangala", pincode: "560038", lat: 12.9716, lng: 77.5946 },
  { city: "Chandigarh", state: "Chandigarh UT", area: "Sector 17 / Sector 35", pincode: "160017", lat: 30.7333, lng: 76.7794 },
  { city: "Mohali (SAS Nagar)", state: "Punjab", area: "Phase 3B2 / Phase 7", pincode: "160055", lat: 30.7046, lng: 76.7179 },
  { city: "Jaipur", state: "Rajasthan", area: "MI Road / Malviya Nagar", pincode: "302001", lat: 26.9124, lng: 75.7873 },
  { city: "Pune", state: "Maharashtra", area: "FC Road / Koregaon Park", pincode: "411004", lat: 18.5204, lng: 73.8567 },
  { city: "Hyderabad", state: "Telangana", area: "Banjara Hills / Jubilee Hills", pincode: "500034", lat: 17.3850, lng: 78.4867 },
  { city: "Kolkata", state: "West Bengal", area: "Park Street / Salt Lake", pincode: "700016", lat: 22.5726, lng: 88.3639 },
  { city: "Ahmedabad", state: "Gujarat", area: "Navrangpura / SG Highway", pincode: "380009", lat: 23.0225, lng: 72.5714 },
  { city: "Lucknow", state: "Uttar Pradesh", area: "Hazratganj / Gomti Nagar", pincode: "226001", lat: 26.8467, lng: 80.9462 },
  { city: "Amritsar", state: "Punjab", area: "Hall Bazaar / Ranjit Avenue", pincode: "143001", lat: 31.6340, lng: 74.8723 },
  { city: "Jalandhar", state: "Punjab", area: "Model Town / Cantt", pincode: "144001", lat: 31.3260, lng: 75.5762 },
  { city: "Noida", state: "Uttar Pradesh", area: "Sector 18 / Atta Market", pincode: "201301", lat: 28.5355, lng: 77.3910 },
  { city: "Gurgaon", state: "Haryana", area: "Cyber City / Galleria", pincode: "122002", lat: 28.4595, lng: 77.0266 },
  { city: "Indore", state: "Madhya Pradesh", area: "Vijay Nagar / MG Road", pincode: "452010", lat: 22.7196, lng: 75.8577 },
  { city: "Kochi", state: "Kerala", area: "MG Road / Panampilly Nagar", pincode: "682016", lat: 9.9312, lng: 76.2673 },
];

// Curated Indian States with their prominent cities
const STATE_DIRECTORY = [
  {
    state: "Punjab",
    cities: [
      { city: "Ahmedgarh", area: "Main Market / Ward 11", pincode: "148021", lat: 30.6826, lng: 75.8302 },
      { city: "Ludhiana", area: "Ghumar Mandi / Model Town", pincode: "141001", lat: 30.9010, lng: 75.8573 },
      { city: "Amritsar", area: "Hall Bazaar / Ranjit Avenue", pincode: "143001", lat: 31.6340, lng: 74.8723 },
      { city: "Jalandhar", area: "Model Town / Cantt", pincode: "144001", lat: 31.3260, lng: 75.5762 },
      { city: "Malerkotla", area: "Satta Chowk / Sirhindi Gate", pincode: "148023", lat: 30.5283, lng: 75.8893 },
      { city: "Patiala", area: "Baradari / Mall Road", pincode: "147001", lat: 30.3398, lng: 76.3869 },
      { city: "Bathinda", area: "Mall Road / Model Town", pincode: "151001", lat: 30.2110, lng: 74.9455 },
      { city: "Mohali (SAS Nagar)", area: "Phase 3B2 / Phase 7", pincode: "160055", lat: 30.7046, lng: 76.7179 },
      { city: "Sangrur", area: "Sunami Gate / Kaula Park", pincode: "148001", lat: 30.2458, lng: 75.8421 },
    ],
  },
  {
    state: "Delhi NCR",
    cities: [
      { city: "Central Delhi", area: "Connaught Place / Karol Bagh", pincode: "110001", lat: 28.6139, lng: 77.2090 },
      { city: "South Delhi", area: "Saket / Hauz Khas / GK", pincode: "110017", lat: 28.5245, lng: 77.2066 },
      { city: "Noida", area: "Sector 18 / Atta Market", pincode: "201301", lat: 28.5355, lng: 77.3910 },
      { city: "Gurgaon (Gurugram)", area: "Cyber City / Galleria", pincode: "122002", lat: 28.4595, lng: 77.0266 },
      { city: "Faridabad", area: "NIT / Sector 15", pincode: "121001", lat: 28.4089, lng: 77.3178 },
      { city: "Ghaziabad", area: "Indirapuram / Raj Nagar", pincode: "201014", lat: 28.6692, lng: 77.4538 },
    ],
  },
  {
    state: "Maharashtra",
    cities: [
      { city: "Mumbai", area: "Bandra / Andheri", pincode: "400050", lat: 19.0760, lng: 72.8777 },
      { city: "Pune", area: "FC Road / Koregaon Park", pincode: "411004", lat: 18.5204, lng: 73.8567 },
      { city: "Nagpur", area: "Dharampeth / Sitabuldi", pincode: "440010", lat: 21.1458, lng: 79.0882 },
      { city: "Nashik", area: "College Road / Gangapur", pincode: "422005", lat: 19.9975, lng: 73.7898 },
      { city: "Thane", area: "Viviana / Ghodbunder", pincode: "400601", lat: 19.2183, lng: 72.9781 },
      { city: "Navi Mumbai", area: "Vashi / Nerul", pincode: "400703", lat: 19.0330, lng: 73.0297 },
      { city: "Sawantwadi", area: "Main Market / Moti Talao", pincode: "416500", lat: 15.9080, lng: 73.8205 },
      { city: "Kolhapur", area: "Rajarampuri / Mahalaxmi", pincode: "416001", lat: 16.7050, lng: 74.2433 },
      { city: "Solapur", area: "Navi Peth / Murarji Peth", pincode: "413001", lat: 17.6599, lng: 75.9064 },
      { city: "Aurangabad", area: "Cannaught Place / CIDCO", pincode: "431001", lat: 19.8762, lng: 75.3433 },
    ],
  },
  {
    state: "Karnataka",
    cities: [
      { city: "Bengaluru", area: "Indiranagar / Koramangala", pincode: "560038", lat: 12.9716, lng: 77.5946 },
      { city: "Mysuru", area: "Devaraja / Gokulam", pincode: "570001", lat: 12.2958, lng: 76.6394 },
      { city: "Mangaluru", area: "Hampankatta / Lalbagh", pincode: "575001", lat: 12.9141, lng: 74.8560 },
      { city: "Hubli-Dharwad", area: "CBT / Vidyanagar", pincode: "580020", lat: 15.3647, lng: 75.1240 },
    ],
  },
  {
    state: "Uttar Pradesh",
    cities: [
      { city: "Lucknow", area: "Hazratganj / Gomti Nagar", pincode: "226001", lat: 26.8467, lng: 80.9462 },
      { city: "Kanpur", area: "Swaroop Nagar / Civil Lines", pincode: "208001", lat: 26.4499, lng: 80.3319 },
      { city: "Varanasi", area: "Godowlia / Sigra", pincode: "221001", lat: 25.3176, lng: 82.9739 },
      { city: "Agra", area: "Sadar Bazaar / Sanjay Place", pincode: "282001", lat: 27.1767, lng: 78.0081 },
      { city: "Prayagraj", area: "Civil Lines / Katra", pincode: "211001", lat: 25.4358, lng: 81.8463 },
    ],
  },
  {
    state: "Rajasthan",
    cities: [
      { city: "Jaipur", area: "MI Road / Malviya Nagar", pincode: "302001", lat: 26.9124, lng: 75.7873 },
      { city: "Jodhpur", area: "Sardarpura / Sohati Gate", pincode: "342003", lat: 26.2389, lng: 73.0243 },
      { city: "Udaipur", area: "Fateh Sagar / Bapu Bazaar", pincode: "313001", lat: 24.5854, lng: 73.7125 },
      { city: "Kota", area: "Gumanpura / Aerodrome Circle", pincode: "324007", lat: 25.2138, lng: 75.8648 },
    ],
  },
  {
    state: "Gujarat",
    cities: [
      { city: "Ahmedabad", area: "Navrangpura / SG Highway", pincode: "380009", lat: 23.0225, lng: 72.5714 },
      { city: "Surat", area: "Athwa / Ring Road", pincode: "395007", lat: 21.1702, lng: 72.8311 },
      { city: "Vadodara", area: "Alkapuri / Sayajigunj", pincode: "390007", lat: 22.3072, lng: 73.1812 },
      { city: "Rajkot", area: "Yagnik Road / Kalawad", pincode: "360001", lat: 22.3039, lng: 70.8022 },
    ],
  },
  {
    state: "Haryana",
    cities: [
      { city: "Gurgaon", area: "Sector 29 / DLF Phase 1-5", pincode: "122002", lat: 28.4595, lng: 77.0266 },
      { city: "Faridabad", area: "Sector 15 / Crown Plaza", pincode: "121007", lat: 28.4089, lng: 77.3178 },
      { city: "Panipat", area: "Model Town / GT Road", pincode: "132103", lat: 29.3909, lng: 76.9635 },
      { city: "Ambala", area: "Ambala Cantt / City", pincode: "133001", lat: 30.3782, lng: 76.7767 },
      { city: "Karnal", area: "Sector 12 / Mughal Canal", pincode: "132001", lat: 29.6857, lng: 76.9905 },
    ],
  },
  {
    state: "West Bengal",
    cities: [
      { city: "Kolkata", area: "Park Street / Salt Lake", pincode: "700016", lat: 22.5726, lng: 88.3639 },
      { city: "Howrah", area: "Shibpur / Kadamtala", pincode: "711101", lat: 22.5958, lng: 88.2636 },
      { city: "Siliguri", area: "Sevoke Road / City Centre", pincode: "734001", lat: 26.7271, lng: 88.3953 },
    ],
  },
  {
    state: "Tamil Nadu",
    cities: [
      { city: "Chennai", area: "T. Nagar / Anna Nagar", pincode: "600017", lat: 13.0827, lng: 80.2707 },
      { city: "Coimbatore", area: "RS Puram / Gandhipuram", pincode: "641002", lat: 11.0168, lng: 76.9558 },
      { city: "Madurai", area: "KK Nagar / West Veli", pincode: "625020", lat: 9.9252, lng: 78.1198 },
    ],
  },
  {
    state: "Telangana & Andhra Pradesh",
    cities: [
      { city: "Hyderabad", area: "Banjara Hills / Jubilee Hills", pincode: "500034", lat: 17.3850, lng: 78.4867 },
      { city: "Secunderabad", area: "Paradise / Marredpally", pincode: "500003", lat: 17.4399, lng: 78.4983 },
      { city: "Visakhapatnam", area: "Siripuram / Jagadamba", pincode: "530002", lat: 17.6868, lng: 83.2185 },
      { city: "Vijayawada", area: "MG Road / Benz Circle", pincode: "520010", lat: 16.5062, lng: 80.6480 },
    ],
  },
  {
    state: "Kerala",
    cities: [
      { city: "Kochi", area: "MG Road / Panampilly Nagar", pincode: "682016", lat: 9.9312, lng: 76.2673 },
      { city: "Thiruvananthapuram", area: "Pattom / Kowdiar", pincode: "695004", lat: 8.5241, lng: 76.9366 },
      { city: "Kozhikode", area: "SM Street / Mavoor Road", pincode: "673001", lat: 11.2588, lng: 75.7804 },
    ],
  },
  {
    state: "Madhya Pradesh",
    cities: [
      { city: "Indore", area: "Vijay Nagar / MG Road", pincode: "452010", lat: 22.7196, lng: 75.8577 },
      { city: "Bhopal", area: "MP Nagar / New Market", pincode: "462011", lat: 23.2599, lng: 77.4126 },
      { city: "Gwalior", area: "Lashkar / City Centre", pincode: "474001", lat: 26.2183, lng: 78.1828 },
    ],
  },
  {
    state: "Bihar & Jharkhand",
    cities: [
      { city: "Patna", area: "Boring Road / Fraser Road", pincode: "800001", lat: 25.5941, lng: 85.1376 },
      { city: "Ranchi", area: "Main Road / Lalpur", pincode: "834001", lat: 23.3441, lng: 85.3096 },
      { city: "Jamshedpur", area: "Bistupur / Sakchi", pincode: "831001", lat: 22.8046, lng: 86.2029 },
    ],
  },
  {
    state: "Himachal & J&K",
    cities: [
      { city: "Shimla", area: "Mall Road / Ridge", pincode: "171001", lat: 31.1048, lng: 77.1734 },
      { city: "Dharamshala", area: "Kotwali / McLeodGanj", pincode: "176215", lat: 32.2190, lng: 76.3234 },
      { city: "Jammu", area: "Gandhi Nagar / Raghunath", pincode: "180001", lat: 32.7266, lng: 74.8570 },
      { city: "Srinagar", area: "Lal Chowk / Rajbagh", pincode: "190001", lat: 34.0837, lng: 74.7973 },
    ],
  },
  {
    state: "Goa",
    cities: [
      { city: "Panaji", area: "MG Road / Fontainhas", pincode: "403001", lat: 15.4909, lng: 73.8278 },
      { city: "Margao", area: "Pajifond / Municipal Market", pincode: "403601", lat: 15.2832, lng: 73.9862 },
      { city: "Vasco da Gama", area: "Swatantra Path / FL Gomes", pincode: "403802", lat: 15.3982, lng: 73.8113 },
      { city: "Mapusa", area: "Municipal Market / Feira Baixa", pincode: "403507", lat: 15.5937, lng: 73.8142 },
    ],
  },
  {
    state: "Uttarakhand",
    cities: [
      { city: "Dehradun", area: "Paltan Bazaar / Rajpur Road", pincode: "248001", lat: 30.3165, lng: 78.0322 },
      { city: "Haridwar", area: "Har Ki Pauri / Ranipur", pincode: "249401", lat: 29.9457, lng: 78.1642 },
      { city: "Rishikesh", area: "Triveni Ghat / Laxman Jhula", pincode: "249201", lat: 30.0869, lng: 78.2676 },
      { city: "Haldwani", area: "Nainital Road / Tikonia", pincode: "263139", lat: 29.2183, lng: 79.5130 },
    ],
  },
];

// Flat list for fast local search
const ALL_SEARCHABLE_LOCATIONS = [];
const seenLocs = new Set();
const registerLoc = (item, defaultState) => {
  const key = `${(item.city || '').toLowerCase()}-${item.pincode || ''}`;
  if (!seenLocs.has(key)) {
    seenLocs.add(key);
    ALL_SEARCHABLE_LOCATIONS.push({
      ...item,
      state: item.state || defaultState || '',
    });
  }
};
POPULAR_CITIES.forEach((c) => registerLoc(c, c.state));
STATE_DIRECTORY.forEach((st) => {
  st.cities.forEach((c) => registerLoc(c, st.state));
});

/**
 * Live nationwide place search across India via free OpenStreetMap Nominatim.
 */
async function searchNominatimPlaces(query, signal) {
  if (!query || query.trim().length < 2) return [];
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      query.trim()
    )}&countrycodes=in&format=json&addressdetails=1&limit=8`;
    const res = await fetch(url, {
      signal,
      headers: {
        'Accept-Language': 'en',
        'User-Agent': 'IMOnlineShop/1.0 (imonline.shop)',
      },
    });
    if (!res.ok) return [];
    const json = await res.json();
    if (!Array.isArray(json)) return [];

    return json
      .map((item) => {
        const a = item.address || {};
        const city =
          a.city ||
          a.town ||
          a.municipality ||
          a.village ||
          a.suburb ||
          a.city_district ||
          item.name;

        const state = a.state || a.state_district || '';
        const area =
          a.suburb ||
          a.neighbourhood ||
          a.quarter ||
          a.village ||
          a.county ||
          a.road ||
          city;

        const pincode = a.postcode || '';
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.lon);

        return {
          city: city || item.name,
          state: state,
          area: area !== city ? area : (state ? `${city}, ${state}` : city),
          pincode: pincode,
          lat: isNaN(lat) ? null : lat,
          lng: isNaN(lng) ? null : lng,
          source: 'osm',
          displayName: item.display_name,
        };
      })
      .filter((p) => p.lat !== null && p.lng !== null);
  } catch (err) {
    if (err.name === 'AbortError') return [];
    return [];
  }
}

function LocationModal() {
  const {
    location,
    isLocationModalOpen,
    setIsLocationModalOpen,
    detectCurrentLocation,
    setManualLocation,
    isDetecting,
    gpsStatus,
  } = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchingOnline, setIsSearchingOnline] = useState(false);
  const [onlineResults, setOnlineResults] = useState([]);
  const [activeTab, setActiveTab] = useState('popular'); // 'popular' | 'states' | 'custom'
  const [expandedState, setExpandedState] = useState('Punjab');
  const [customCity, setCustomCity] = useState('');
  const [customPincode, setCustomPincode] = useState('');

  // Reset fields when opened
  useEffect(() => {
    if (isLocationModalOpen) {
      setSearchQuery('');
      setOnlineResults([]);
      setIsSearchingOnline(false);
    }
  }, [isLocationModalOpen]);

  // Live nationwide geocode search with 280ms debounce
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setOnlineResults([]);
      setIsSearchingOnline(false);
      return;
    }

    const controller = new AbortController();
    setIsSearchingOnline(true);
    const timer = setTimeout(async () => {
      try {
        const places = await searchNominatimPlaces(q, controller.signal);
        if (!controller.signal.aborted) {
          setOnlineResults(places);
        }
      } catch {
        // ignore
      } finally {
        if (!controller.signal.aborted) {
          setIsSearchingOnline(false);
        }
      }
    }, 280);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  // Merge fast local matches with live Nominatim places
  const combinedResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];

    const localMatches = ALL_SEARCHABLE_LOCATIONS.filter((item) => {
      const cityMatch = item.city.toLowerCase().includes(q);
      const stateMatch = item.state && item.state.toLowerCase().includes(q);
      const areaMatch = item.area && item.area.toLowerCase().includes(q);
      const pinMatch = item.pincode && item.pincode.includes(q);
      return cityMatch || stateMatch || areaMatch || pinMatch;
    });

    const seen = new Set();
    const merged = [];

    for (const item of localMatches) {
      const key = `${item.city.toLowerCase()}-${item.pincode || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    }

    for (const item of onlineResults) {
      const key = `${item.city.toLowerCase()}-${item.pincode || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    }

    return merged.slice(0, 16);
  }, [searchQuery, onlineResults]);

  if (!isLocationModalOpen) return null;

  // Handler for selecting any city
  const handleSelectCity = (item) => {
    const label = `${item.city}${item.pincode ? ' ' + item.pincode : ''}`.trim();
    setManualLocation({
      city: item.city,
      area: item.area || item.state || item.city,
      state: item.state || '',
      pincode: item.pincode || '',
      lat: item.lat || null,
      lng: item.lng || null,
      label: label,
      radius: 'auto',
    });
    setIsLocationModalOpen(false);
  };

  // Handler for All India
  const handleAllIndia = () => {
    setManualLocation({
      city: "All Locations",
      area: "Across India",
      pincode: "",
      lat: null,
      lng: null,
      label: "All Locations",
      radius: 9999,
    });
    setIsLocationModalOpen(false);
  };

  // Handler for explicit GPS detection click
  const handleDetectGPS = async () => {
    await detectCurrentLocation();
    // Modal will be closed by detectCurrentLocation in LocationContext upon success
  };

  // Handler for custom city / pincode form
  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const city = customCity.trim();
    const pin = customPincode.trim();
    if (!city && !pin) return;

    // Check if entered pincode matches any known city
    let matched = ALL_SEARCHABLE_LOCATIONS.find(
      (l) => l.pincode === pin || (city && l.city.toLowerCase() === city.toLowerCase())
    );

    const chosenCity = matched ? matched.city : (city || `Pincode ${pin}`);
    const chosenArea = matched ? matched.area : (city ? `Area ${city}` : `Pincode Area`);
    const chosenLat = matched ? matched.lat : null;
    const chosenLng = matched ? matched.lng : null;

    setManualLocation({
      city: chosenCity,
      area: chosenArea,
      pincode: pin,
      lat: chosenLat,
      lng: chosenLng,
      label: `${chosenCity}${pin ? ' ' + pin : ''}`.trim(),
      radius: 10,
    });
    setIsLocationModalOpen(false);
  };

  return (
    <div
      className="loc-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setIsLocationModalOpen(false);
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-modal-title"
    >
      <div className="loc-modal-card">
        {/* Header */}
        <div className="loc-modal-header">
          <div className="loc-modal-title-wrap">
            <span className="loc-modal-tag">Local Shopping Delivery</span>
            <h3 id="location-modal-title" className="loc-modal-title">
              Choose Your Location
            </h3>
          </div>
          <button
            type="button"
            className="loc-modal-close-btn"
            onClick={() => setIsLocationModalOpen(false)}
            aria-label="Close location selector"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="loc-modal-body">
          {/* Search Box */}
          <div className="loc-search-box">
            <SearchIcon className="loc-search-icon" />
            <input
              type="text"
              className="loc-search-input"
              placeholder="Search City, Town, State or Pincode across India..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (combinedResults.length > 0) {
                    handleSelectCity(combinedResults[0]);
                  }
                }
              }}
              autoFocus
            />
            {isSearchingOnline && (
              <span className="loc-search-spinner" title="Searching places across India...">
                <span className="loc-mini-spinner" />
              </span>
            )}
            {searchQuery && !isSearchingOnline && (
              <button
                type="button"
                className="loc-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </div>

          {/* Quick Actions (GPS & All India) */}
          {!searchQuery && (
            <div className="loc-quick-actions">
              <button
                type="button"
                className={`loc-action-btn loc-action-gps ${
                  gpsStatus === 'granted' ? 'active-gps' : ''
                }`}
                onClick={handleDetectGPS}
                disabled={isDetecting}
                title="Detect GPS location from your browser"
              >
                <MyLocationIcon fontSize="small" />
                <span>
                  {isDetecting
                    ? 'Detecting Location...'
                    : gpsStatus === 'granted'
                    ? `✓ ${location.city || 'Current GPS'}`
                    : 'Use Current Location'}
                </span>
              </button>

              <button
                type="button"
                className="loc-action-btn loc-action-all"
                onClick={handleAllIndia}
                title="Browse shops across all India"
              >
                <PublicIcon fontSize="small" />
                <span>All India (Any Area)</span>
              </button>
            </div>
          )}

          {/* If Search Query is Typed: Show Instant & Live Results */}
          {searchQuery ? (
            <div className="loc-results-list">
              {combinedResults.length > 0 ? (
                combinedResults.map((item, idx) => (
                  <button
                    key={`${item.city}-${item.pincode}-${idx}`}
                    type="button"
                    className="loc-result-item"
                    onClick={() => handleSelectCity(item)}
                  >
                    <div className="loc-result-left">
                      <LocationOnIcon className="loc-result-pin" />
                      <div className="loc-result-details">
                        <span className="loc-result-name">
                          {item.city}{item.state ? `, ${item.state}` : ''}
                        </span>
                        <span className="loc-result-sub">
                          {item.area} {item.pincode ? `• PIN: ${item.pincode}` : ''}
                        </span>
                      </div>
                    </div>
                    {item.pincode && (
                      <span className="loc-result-badge">{item.pincode}</span>
                    )}
                  </button>
                ))
              ) : isSearchingOnline ? (
                <div style={{ textAlign: 'center', padding: '24px 16px', color: '#6b7280' }}>
                  <div className="loc-searching-indicator">
                    <span className="loc-mini-spinner" />
                    <span>Searching places and towns across India...</span>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 16px', color: '#6b7280' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: '600' }}>
                    No exact match found for "{searchQuery}".
                  </p>
                  <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#9ca3af' }}>
                    You can still use this as your location area.
                  </p>
                  <button
                    type="button"
                    className="loc-apply-btn"
                    onClick={async () => {
                      const q = searchQuery.trim();
                      let matched = null;
                      try {
                        const found = await searchNominatimPlaces(q);
                        if (found && found.length > 0) matched = found[0];
                      } catch {
                        // ignore
                      }
                      setManualLocation({
                        city: matched ? matched.city : q,
                        area: matched ? matched.area : "Custom Location",
                        pincode: matched ? matched.pincode : "",
                        lat: matched ? matched.lat : null,
                        lng: matched ? matched.lng : null,
                        label: q,
                        radius: 'auto',
                      });
                      setIsLocationModalOpen(false);
                    }}
                  >
                    Set "{searchQuery}" as My Location
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Tabs: Popular Cities, Browse by State, Custom Pincode */
            <div>
              <div className="loc-tabs">
                <button
                  type="button"
                  className={`loc-tab-btn ${activeTab === 'popular' ? 'active' : ''}`}
                  onClick={() => setActiveTab('popular')}
                >
                  Popular Cities
                </button>
                <button
                  type="button"
                  className={`loc-tab-btn ${activeTab === 'states' ? 'active' : ''}`}
                  onClick={() => setActiveTab('states')}
                >
                  Browse by State
                </button>
                <button
                  type="button"
                  className={`loc-tab-btn ${activeTab === 'custom' ? 'active' : ''}`}
                  onClick={() => setActiveTab('custom')}
                >
                  Custom Area
                </button>
              </div>

              {/* TAB 1: Popular Cities Grid */}
              {activeTab === 'popular' && (
                <div className="loc-cities-grid">
                  {POPULAR_CITIES.map((item, idx) => {
                    const isSelected =
                      location &&
                      location.city &&
                      location.city.toLowerCase() === item.city.toLowerCase();

                    return (
                      <button
                        key={idx}
                        type="button"
                        className="loc-city-card"
                        onClick={() => handleSelectCity(item)}
                        style={{
                          borderColor: isSelected ? '#a85050' : '#e5e7eb',
                          background: isSelected ? '#fff5f5' : '#ffffff',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span className="loc-city-name">{item.city}</span>
                          {isSelected && (
                            <CheckCircleIcon style={{ fontSize: '16px', color: '#a85050' }} />
                          )}
                        </div>
                        <span className="loc-city-area">{item.state} • {item.area}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* TAB 2: Browse by State Accordion */}
              {activeTab === 'states' && (
                <div className="loc-states-list">
                  {STATE_DIRECTORY.map((st) => {
                    const isExpanded = expandedState === st.state;

                    return (
                      <div key={st.state} className="loc-state-group">
                        <button
                          type="button"
                          className="loc-state-header"
                          onClick={() => setExpandedState(isExpanded ? '' : st.state)}
                        >
                          <span>{st.state} ({st.cities.length} Cities)</span>
                          {isExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="loc-state-cities">
                            {st.cities.map((cityObj, cIdx) => (
                              <button
                                key={cIdx}
                                type="button"
                                className="loc-state-city-pill"
                                onClick={() => handleSelectCity({ ...cityObj, state: st.state })}
                              >
                                {cityObj.city}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* TAB 3: Custom Pincode / Area Form */}
              {activeTab === 'custom' && (
                <form onSubmit={handleCustomSubmit} className="loc-custom-form">
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#111315' }}>
                    Enter Specific City, Town, or 6-digit Pincode:
                  </span>
                  <div className="loc-custom-row">
                    <input
                      type="text"
                      className="loc-input-field"
                      style={{ flex: 1.5 }}
                      placeholder="City or Town (e.g. Ahmedgarh, Solan)"
                      value={customCity}
                      onChange={(e) => setCustomCity(e.target.value)}
                    />
                    <input
                      type="text"
                      className="loc-input-field"
                      style={{ flex: 1 }}
                      placeholder="6-digit Pincode"
                      maxLength="6"
                      value={customPincode}
                      onChange={(e) =>
                        setCustomPincode(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                    />
                  </div>
                  <button type="submit" className="loc-apply-btn">
                    Apply Location
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationModal;
